import { Tooltip } from "antd";
import classNames from "classnames";
import { motion } from "framer-motion";
import { FC } from "react";
import { BiCheck } from "react-icons/bi";
import styles from "./card.module.scss";
import CardImageLoader from "@/features/bridge/components/token-selector/selector-interface/card-image-loader";
import { useNftTokenManager } from "@/features/bridge/stores/nft-token-manager";
import type { ModifiedNftToken } from "@/features/bridge/types/open-sea-api.types";
import { useTokenEligibility, getEligibilityMessage } from "@/features/bridge/hooks/useTokenEligibility.hook";
import { useBridge } from "@/features/bridge/providers/bridge.provider";

type Props = {
  token: ModifiedNftToken;
  collection: string;
};

const NftCard: FC<Props> = ({ token }) => {
  const { toggleToken, getSelectionStatus } = useNftTokenManager();
  const { bridgeControls } = useBridge();

  const isActive = getSelectionStatus(token);

  // Check token eligibility for bridging
  const eligibility = useTokenEligibility(token.address);
  const eligibilityMessage = getEligibilityMessage(eligibility);

  const handleClick = () => {
    // Don't allow interaction if token is not eligible
    if (!eligibility.isEligible) return;
    toggleToken(token);
  };

  return (
    <Tooltip
      title={!eligibility.isEligible ? eligibilityMessage : undefined}
      placement={'center' as any}
    >
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        disabled={!token || !eligibility.isEligible}
        className={classNames(
          styles.cardContainer,
          isActive && styles.active,
          !eligibility.isEligible && styles.disabled
        )}
        onClick={handleClick}
      >
        <div className={styles.image}>
          <CardImageLoader
            src={token.display_image_url}
            alt={token.name + " display image"}
            identifier={token.identifier}
            symbol={token.symbol}
            contractAddress={token.address}
            chainId={bridgeControls.fromChain.originChain?.chainId}
            tokenType={token.token_standard}
          />
          <div className={classNames(styles.tick, isActive && styles.active)}>
            <BiCheck size={25} />
          </div>
        </div>
        <div className={styles.text}>
          <p className={styles.cardTitle}>
            {token.name ? token.name : token.identifier}
          </p>
          <p className={styles.tokenId}>
            #{token.identifier}
          </p>
        </div>
      </motion.button>
    </Tooltip>
  );
};

export default NftCard;
